import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  model,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TooltipDirective } from '@catch/directives';
import { FileInputComponent } from '@catch/file-input';
import { InfoBannerComponent, ModalComponent } from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { LottieComponent } from 'ngx-lottie';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { catchError, of } from 'rxjs';
import { viewLoader } from '../../../assets/animations/animations';
import { AppState } from '../../app.reducer';
import { transitionEasing, transitionString } from '../../constants/transition';
import { AiBorderDirective } from '../../directives/ai-border.directive';
import { StickToBottomDirective } from '../../directives/stick-to-bottom.directive';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { Job } from '../../interfaces/job';
import { JobPosting } from '../../interfaces/job-posting';
import { CompanionChatService } from '../../services/companion-chat.service';
import { CompanionService } from '../../services/companion.service';
import { CompanionVoiceInputService } from '../../services/companion-voice-input.service';
import { FontawesomeService } from '../../services/fontawesome.service';
import {
  loadConnection,
  loadConnectionJobs,
} from '../../store/connection/connection.actions';
import {
  selectConnection,
  selectConnectionJobs,
} from '../../store/connection/connection.reducer';
import { loadJobs } from '../../store/jobs/jobs.actions';
import { selectJobsAll } from '../../store/jobs/jobs.reducer';
import { selectUserAccount } from '../../store/user-account/user-account.reducer';
import { loadUserAvatar } from '../../store/user-avatar/user-avatar.actions';
import { selectUserAvatarUrl } from '../../store/user-avatar/user-avatar.reducer';
import { AvatarPlaceholderComponent } from '../avatar-placeholder/avatar-placeholder.component';
import { BlobComponent } from '../blob/blob.component';
import { ButtonComponent } from '../button/button.component';
import { CompanionChatThreadComponent } from './companion-chat-thread.component';
import { CompanionRole } from './companion.enum';

@UntilDestroy()
@Component({
  selector: 'app-companion',
  templateUrl: './companion.component.html',
  styleUrls: ['./companion.component.scss'],
  imports: [
    TranslocoPipe,
    FontAwesomeModule,
    CommonModule,
    FormsModule,
    ModalComponent,
    InfoBannerComponent,
    FileInputComponent,
    ButtonComponent,
    TooltipDirective,
    LottieComponent,
    BlobComponent,
    AiBorderDirective,
    StickToBottomDirective,
    TourMatMenu,
    CompanionChatThreadComponent,
  ],
  animations: [
    viewLoader,
    trigger('companionAnimation', [
      state(
        'void',
        style({
          transform: 'translateX(160px)',
          filter: 'blur(10px)',
          opacity: 0,
        }),
      ),
      state(
        '*',
        style({
          transform: 'translateX(0)',
          filter: 'blur(0px)',
          opacity: 1,
        }),
      ),
      transition(':enter', [animate(transitionString)]),
      transition(':leave', [animate(transitionString)]),
    ]),
    trigger('actionBarAnimation', [
      state(
        'void',
        style({
          height: '0',
          opacity: '0',
        }),
      ),
      state(
        '*',
        style({
          height: '*',
          opacity: '1',
        }),
      ),
      transition(':enter', [animate(transitionString)]),
      transition(':leave', [animate(transitionString)]),
    ]),
    trigger('messageAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.9) translateY(10px)',
        }),
        animate(
          `150ms ${transitionEasing}`,
          style({
            opacity: 0.66,
            transform: 'scale(1.02)',
          }),
        ),
        animate(
          `50ms ${transitionEasing}`,
          style({
            opacity: 1,
            transform: 'scale(1)',
          }),
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanionComponent implements OnInit, OnDestroy {
  private readonly translocoService = inject(TranslocoService);
  public readonly companionService = inject(CompanionService);
  public readonly companionChat = inject(CompanionChatService);
  public readonly companionVoice = inject(CompanionVoiceInputService);
  public readonly domSanitizer = inject(DomSanitizer);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly store = inject(Store<AppState>);
  private readonly jobsAdapter = inject(JobsAdapter);

  @ViewChild('messageStick') messageStick?: StickToBottomDirective;
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('loadJobFromATSModal') loadJobFromATSModal!: ModalComponent;
  @ViewChild('loadJobFromTemplateModal')
  loadJobFromTemplateModal!: ModalComponent;
  @ViewChild('loadJobFromPdfModal') loadJobFromPdfModal!: ModalComponent;

  public readonly MAX_LENGTH_CHARACTER_LIMIT =
    this.companionChat.MAX_LENGTH_CHARACTER_LIMIT;

  public readonly CompanionRole = CompanionRole;

  public readonly voiceSupported = this.companionVoice.voiceSupported;
  public readonly isListening = this.companionVoice.isListening;
  public readonly voiceError = this.companionVoice.voiceError;
  public readonly waveformLevels = this.companionVoice.waveformLevels;

  public avatarUrl = this.store.selectSignal(selectUserAvatarUrl);
  public userAccount = this.store.selectSignal(selectUserAccount);
  public connection = this.store.selectSignal(selectConnection);
  public connectionJobs = this.store.selectSignal(selectConnectionJobs);
  public jobs = this.store.selectSignal(selectJobsAll);
  public archivedJobs = signal<Job[]>([]);

  public atsJobsSearchTerm = model<string>('');
  public templateJobsSearchTerm = model<string>('');

  public filteredATSJobs = computed(() => {
    const searchTerm = this.atsJobsSearchTerm().toLowerCase();
    const jobs = this.connectionJobs();

    if (!searchTerm) {
      return jobs;
    }

    return jobs.filter(
      (job) =>
        job.title.toLowerCase().includes(searchTerm) ||
        job.remoteID?.toLowerCase().includes(searchTerm) ||
        job.workLocation?.city?.toLowerCase().includes(searchTerm),
    );
  });

  public filteredTemplateJobs = computed(() => {
    const searchTerm = this.templateJobsSearchTerm().toLowerCase();
    const jobs = [...this.jobs(), ...this.archivedJobs()];

    if (!searchTerm) {
      return jobs;
    }

    return jobs.filter(
      (job) =>
        job.title.toLowerCase().includes(searchTerm) ||
        job.id.toString().includes(searchTerm),
    );
  });

  public get chat() {
    return this.companionChat.chat;
  }

  public get loading() {
    return this.companionChat.loading;
  }

  public get errorWhileAnalyzing() {
    return this.companionChat.errorWhileAnalyzing;
  }

  public get errorWhileReplying() {
    return this.companionChat.errorWhileReplying;
  }

  public get loadingLottie() {
    return this.companionChat.loadingLottie;
  }

  public get errorLottie() {
    return this.companionChat.errorLottie;
  }

  public readonly textValue = this.companionChat.textValue;

  public get isKiraBusy() {
    return this.companionChat.isKiraBusy;
  }

  public canSubmitMessage = computed(() => {
    return this.companionChat.canSubmitMessage() && !this.isListening();
  });

  public canUseVoice = computed(
    () =>
      this.voiceSupported() &&
      !this.isKiraBusy() &&
      !this.loading() &&
      !this.errorWhileAnalyzing() &&
      !this.companionService.promptingModalOpen(),
  );

  public inputPlaceholderKey = computed(() => {
    if (this.isListening()) {
      return 'company-tool.snippets.companion.input.placeholder-listening';
    }
    return 'company-tool.snippets.companion.input.placeholder';
  });

  /** Hide empty-state suggestions while the prompting modal owns the create flow. */
  public showEmptyState = computed(
    () =>
      !this.companionChat.hasMessages() &&
      !this.companionService.promptingModalOpen(),
  );

  readonly emptyStateSuggestionRows = [
    [
      'company-tool.snippets.companion.empty-state.suggestions.lets-post-a-job',
      'company-tool.snippets.companion.empty-state.suggestions.how-active-sourcing-works',
      'company-tool.snippets.companion.empty-state.suggestions.best-practice-process',
      'company-tool.snippets.companion.empty-state.suggestions.how-to-invite-colleagues',
    ],
    [
      'company-tool.snippets.companion.empty-state.suggestions.how-fast-get-candidates',
      'company-tool.snippets.companion.empty-state.suggestions.find-matching-candidates',
      'company-tool.snippets.companion.empty-state.suggestions.how-talent-pool-works',
      'company-tool.snippets.companion.empty-state.suggestions.how-to-adapt-application-process',
    ],
  ] as const;

  constructor() {
    effect(() => {
      this.companionChat.focusRequest();
      if (!this.companionService.promptingModalOpen()) {
        this.focusMessageInput();
      }
    });
  }

  ngOnInit(): void {
    this.companionVoice.detectSupport();
    this.store.dispatch(loadUserAvatar());
    this.store.dispatch(loadJobs());
    this.store.dispatch(loadConnection());
    this.store.dispatch(loadConnectionJobs());
    this.jobsAdapter
      .getArchivedJobs()
      .pipe(
        catchError(() => of([])),
        untilDestroyed(this),
      )
      .subscribe((jobs) => this.archivedJobs.set(jobs ?? []));
    this.focusMessageInput();

    this.companionService.openLoadJobFromAts
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadJobFromATSModal?.open());
    this.companionService.openLoadJobFromPdf
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadJobFromPdfModal?.open());
    this.companionService.openLoadJobFromTemplate
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadJobFromTemplateModal?.open());
  }

  ngOnDestroy(): void {
    this.companionVoice.stop(false);
  }

  /**
   * Sends a message via the shared chat service.
   * @param {string} message Message text
   * @param {boolean} silent Silent/import payload
   * @param {boolean} isTalentpoolSearchStart Talentpool start flag
   * @returns {Promise<void>}
   */
  async sendMessage(
    message = this.textValue(),
    silent = false,
    isTalentpoolSearchStart = false,
  ): Promise<void> {
    if (this.isListening()) {
      return;
    }
    this.messageStick?.stick();
    await this.companionChat.sendMessage(
      message,
      silent,
      isTalentpoolSearchStart,
    );
  }

  onMessageInputEnter(event: KeyboardEvent): void {
    if (event.shiftKey) {
      return;
    }

    event.preventDefault();
    void this.sendMessage();
  }

  sendEmptyStateSuggestion(key: string): void {
    const translated = this.translocoService.translate(key);
    void this.sendMessage(translated);
  }

  /**
   * Toggles the companion open state through the companion service.
   * @returns {void}
   */
  toggle(): void {
    if (this.companionService.toggleCompanion()) {
      setTimeout(() => this.messageStick?.stick());
      this.focusMessageInput();
    } else {
      this.companionVoice.stop(false);
    }
  }

  closeCompanion(): void {
    this.companionVoice.stop(false);
    this.companionService.closeCompanion();
  }

  /**
   * Focuses the message input.
   * @returns {void}
   */
  focusMessageInput(): void {
    setTimeout(() => {
      this.messageInput?.nativeElement.focus();
    }, 0);
  }

  /**
   * Handles the click on a message (for action or route buttons).
   * @param {Event} event The click event
   * @returns {void}
   */
  handleMessageClick(event: Event): void {
    this.companionChat.handleMessageClick(event);
  }

  /**
   * Handles PDF selection.
   * @param {File} file The selected file
   * @returns {void}
   */
  onPdfSelected(file: File): void {
    this.companionChat.onPdfSelected(file);
  }

  /**
   * Handles ATS job selection.
   * @param {JobPosting} job The selected job
   * @returns {void}
   */
  onATSJobSelected(job: JobPosting): void {
    this.companionChat.onATSJobSelected(job);
    this.loadJobFromATSModal.close();
  }

  /**
   * Handles template job selection.
   * @param {Job} job The selected job
   * @returns {void}
   */
  onTemplateJobSelected(job: Job): void {
    this.companionChat.onTemplateJobSelected(job);
    this.loadJobFromTemplateModal.close();
  }

  /**
   * Parses a message part by tag.
   * @param {string} message Message to parse
   * @param {string} tag Tag name
   * @returns {string} Parsed content
   */
  parseMessagePart(message: string, tag: string = 'message'): string {
    return this.companionChat.parseMessagePart(message, tag);
  }

  /**
   * Parses markdown to HTML.
   * @param {string} message Message to parse
   * @returns {string} HTML string
   */
  parseMarkdown(message: string): string {
    return this.companionChat.parseMarkdown(message);
  }

  getUserInitials(): string {
    const account = this.userAccount();
    return `${account?.firstName?.[0] ?? ''}${account?.lastName?.[0] ?? ''}`.toUpperCase();
  }

  /**
   * Starts or stops live speech recognition for the message input.
   * @returns {void}
   */
  toggleVoiceInput(): void {
    this.companionVoice.toggle(this.canUseVoice(), () => {
      void this.sendMessage();
    });
  }
}
